import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "../lib/api";
import BoxCard from "../components/BoxCard";
import WineCardB2C from "../components/WineCardB2C";
import VendorProductCard from "../components/VendorProductCard";
import { Search as SearchIcon, Package, Boxes, ArrowRight } from "lucide-react";

const matches = (text, q) => (text || "").toLowerCase().includes(q.toLowerCase());

export default function Search() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const [input, setInput] = useState(q);
  const [loading, setLoading] = useState(false);
  const [boxes, setBoxes] = useState([]);
  const [wines, setWines] = useState([]);
  const [vendorB2c, setVendorB2c] = useState([]);
  const [vendorB2b, setVendorB2b] = useState([]);

  const runSearch = useCallback(async (term) => {
    if (!term || term.trim().length < 1) {
      setBoxes([]); setWines([]); setVendorB2c([]); setVendorB2b([]);
      return;
    }
    setLoading(true);
    try {
      const [bx, wn, vd] = await Promise.all([
        api.get("/boxes", { params: { limit: 100 } }).then((r) => r.data).catch(() => []),
        api.get("/wines", { params: { limit: 200 } }).then((r) => r.data).catch(() => []),
        api.get("/vendor/search", { params: { q: term, limit: 100 } }).then((r) => r.data.results || []).catch(() => []),
      ]);
      const bxList = Array.isArray(bx) ? bx : (bx.boxes || bx.items || []);
      const wnList = Array.isArray(wn) ? wn : (wn.wines || wn.items || []);
      setBoxes(bxList.filter((b) => matches(b.name, term) || matches(b.description, term) || matches(b.category, term)));
      setWines(wnList.filter((w) => matches(w.name, term) || matches(w.grape, term) || matches(w.region, term) || matches(w.country, term)));
      setVendorB2c(vd.filter((p) => p.channel === "b2c"));
      setVendorB2b(vd.filter((p) => p.channel === "b2b"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { runSearch(q); }, [q, runSearch]);

  const submit = (e) => {
    e.preventDefault();
    setParams(input ? { q: input } : {});
  };

  const b2cTotal = boxes.length + wines.length + vendorB2c.length;
  const grandTotal = b2cTotal + vendorB2b.length;

  return (
    <div data-testid="search-page" className="container-xl py-12">
      <div className="max-w-2xl mx-auto text-center mb-10">
        <h1 className="editorial-heading text-4xl md:text-5xl">Ricerca</h1>
        <form onSubmit={submit} className="mt-6 relative">
          <SearchIcon className="h-5 w-5 absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            data-testid="search-input"
            autoFocus
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Cerca vini, food box, prodotti dei fornitori…"
            className="w-full pl-12 pr-28 py-4 border border-border rounded-sm bg-card focus:outline-none focus:border-[hsl(var(--primary))]"
          />
          <button
            data-testid="search-submit"
            type="submit"
            className="absolute right-2 top-1/2 -translate-y-1/2 px-5 py-2.5 bg-[hsl(var(--primary))] text-white rounded-sm text-sm font-semibold"
          >
            Cerca
          </button>
        </form>
        {q && (
          <p className="text-muted-foreground mt-4 text-sm">
            {loading ? "Ricerca in corso…" : `${grandTotal} risultati per "${q}"`}
          </p>
        )}
      </div>

      {!q ? (
        <div className="text-center py-16 text-muted-foreground">
          Digita una parola chiave per cercare in tutto il catalogo.
        </div>
      ) : loading ? (
        <div className="text-center py-16 text-muted-foreground">Caricamento…</div>
      ) : grandTotal === 0 ? (
        <div className="text-center py-16">
          <SearchIcon className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">Nessun risultato. Prova con un altro termine.</p>
        </div>
      ) : (
        <div className="space-y-14">
          {/* B2C — a pezzi */}
          {b2cTotal > 0 && (
            <section data-testid="search-b2c-section">
              <div className="flex items-center gap-2 mb-6 border-b border-border pb-3">
                <Package className="h-5 w-5 text-[hsl(var(--primary))]" />
                <h2 className="editorial-heading text-2xl">Prodotti B2C · a pezzi ({b2cTotal})</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {boxes.map((b) => <BoxCard key={`box-${b.id}`} box={b} />)}
                {wines.map((w) => <WineCardB2C key={`wine-${w.id}`} wine={w} />)}
                {vendorB2c.map((p) => <VendorProductCard key={`v-${p.id}`} product={p} showStore />)}
              </div>
            </section>
          )}

          {/* B2B — a blocchi */}
          {vendorB2b.length > 0 && (
            <section data-testid="search-b2b-section">
              <div className="flex items-center justify-between gap-2 mb-6 border-b border-border pb-3">
                <div className="flex items-center gap-2">
                  <Boxes className="h-5 w-5 text-[#8a6d1f]" />
                  <h2 className="editorial-heading text-2xl">Sezione B2B · a blocchi ({vendorB2b.length})</h2>
                </div>
                <Link to="/negozi" className="text-sm text-[hsl(var(--primary))] hover:underline inline-flex items-center gap-1">
                  Vedi le aziende <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
              <div className="bg-[#0a0a0a]/[0.03] border border-[#C9A961]/20 rounded-sm p-4 mb-6 text-sm text-muted-foreground">
                Questi prodotti sono venduti <strong>a blocchi</strong> e riservati agli operatori <strong>B2B</strong>.
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {vendorB2b.map((p) => <VendorProductCard key={`vb-${p.id}`} product={p} showStore />)}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}
